import { useMemo } from "react";
import {
  type Identifier,
  useGetIdentity,
  useGetList,
  useStore,
  useTranslate,
} from "ra-core";
import { useIsMobile } from "@/hooks/use-mobile";

import { ListSkeleton } from "../misc/ListSkeleton";
import type { Task } from "../types";
import { TaskKindFilterControl } from "./TaskKindFilterControl";
import { TaskListFilter } from "./TasksListFilter";
import {
  filterTasksByKind,
  isTaskKindFilter,
  mergeTaskLists,
  TASK_KIND_FILTER_STORE_KEY,
  type TaskKindFilter,
} from "./taskKind";
import {
  isBeforeFriday,
  isDone,
  isDueLater,
  isDueThisWeek,
  isDueToday,
  isDueTomorrow,
  isOverdue,
  isRecentlyDone,
} from "./tasksPredicate";

export const TasksListByDueDate = ({
  filterByContact,
  emptyPlaceholder,
  pendingPlaceholder,
}: {
  filterByContact?: Identifier;
  emptyPlaceholder?: React.ReactNode;
  pendingPlaceholder?: React.ReactNode;
}) => {
  const { identity } = useGetIdentity();
  const isMobile = useIsMobile();
  const translate = useTranslate();

  const showContact = filterByContact == null;
  const [storedKindFilter, setKindFilter] = useStore<TaskKindFilter>(
    TASK_KIND_FILTER_STORE_KEY,
    "all",
  );
  const kindFilter =
    showContact && isTaskKindFilter(storedKindFilter)
      ? storedKindFilter
      : "all";

  const { data: ownTasks, isPending: isOwnPending } = useGetList<Task>(
    "tasks",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "due_date", order: "ASC" },
      filter: {
        ...(filterByContact != null
          ? { contact_id: filterByContact }
          : { sales_id: identity?.id }),
      },
    },
    { enabled: filterByContact != null ? true : !!identity },
  );
  // The global list also shows the team's internal tasks assigned to others,
  // so Jorge sees what he gave Alberto (and the other way round).
  const { data: teamTasks, isPending: isTeamPending } = useGetList<Task>(
    "tasks",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "due_date", order: "ASC" },
      filter: { kind: "internal", "done_date@is": null },
    },
    { enabled: showContact && !!identity },
  );
  const isPending = isOwnPending || (showContact && isTeamPending);

  const ongoingTasks = useMemo(() => {
    const tasks = showContact
      ? mergeTaskLists(ownTasks, teamTasks)
      : (ownTasks ?? []);
    return filterTasksByKind(
      tasks.filter(
        (task) =>
          !isDone({ ...task, done_date: task.done_date ?? null }) ||
          isRecentlyDone({ ...task, done_date: task.done_date ?? null }),
      ),
      kindFilter,
    );
  }, [ownTasks, teamTasks, showContact, kindFilter]);

  const overdueTasks = useMemo(
    () =>
      ongoingTasks?.filter((task) => {
        return isOverdue(task.due_date);
      }) || [],
    [ongoingTasks],
  );

  const dueTodayTasks = useMemo(
    () =>
      ongoingTasks?.filter((task) => {
        return isDueToday(task.due_date);
      }) || [],
    [ongoingTasks],
  );

  const dueTomorrowTasks = useMemo(
    () => ongoingTasks?.filter((task) => isDueTomorrow(task.due_date)) || [],
    [ongoingTasks],
  );

  const dueThisWeekTasks = useMemo(
    () => ongoingTasks?.filter((task) => isDueThisWeek(task.due_date)) || [],
    [ongoingTasks],
  );

  const dueLaterTasks = useMemo(
    () => ongoingTasks?.filter((task) => isDueLater(task.due_date)) || [],
    [ongoingTasks],
  );

  if (isPending) {
    return (
      pendingPlaceholder ?? (
        <ListSkeleton variant="cards" avatar={false} rows={4} />
      )
    );
  }

  const filterControl = showContact ? (
    <TaskKindFilterControl value={kindFilter} onChange={setKindFilter} />
  ) : null;

  if (!ongoingTasks.length) {
    if (kindFilter === "all") {
      return filterControl ? (
        <div className="flex flex-col gap-4">
          {filterControl}
          {emptyPlaceholder}
        </div>
      ) : (
        (emptyPlaceholder ?? null)
      );
    }
    return (
      <div className="flex flex-col gap-4">
        {filterControl}
        <p className="text-sm text-muted-foreground">
          {translate(`resources.tasks.kind.empty_${kindFilter}`)}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {filterControl}
      <TaskListFilter
        tasks={overdueTasks}
        title={translate("resources.tasks.filters.overdue")}
        showContact={showContact}
        isMobile={isMobile}
      />
      <TaskListFilter
        tasks={dueTodayTasks}
        title={translate("resources.tasks.filters.today")}
        showContact={showContact}
        isMobile={isMobile}
      />
      <TaskListFilter
        tasks={dueTomorrowTasks}
        title={translate("resources.tasks.filters.tomorrow")}
        showContact={showContact}
        isMobile={isMobile}
      />
      {(!filterByContact || (filterByContact && isBeforeFriday())) && (
        <TaskListFilter
          tasks={dueThisWeekTasks}
          title={translate("resources.tasks.filters.this_week")}
          showContact={showContact}
          isMobile={isMobile}
        />
      )}
      <TaskListFilter
        tasks={dueLaterTasks}
        title={translate("resources.tasks.filters.later")}
        showContact={showContact}
        isMobile={isMobile}
      />
    </div>
  );
};
